import { Monitor, Moon, Sun } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { ThemeChoice } from "../lib/thememode";
import type { UiLang } from "../lib/types";

interface Props {
  choice: ThemeChoice;
  onChange: (next: ThemeChoice) => void;
  lang: UiLang;
}

/**
 * Rekkefølgen: lyst, mørkt, system.
 *
 * System sist framfor i midten, selv om det er standardvalget. De to første er
 * det man leter etter når man åpner velgeren — man vil ha mørkt *nå* — og
 * «følg systemet» er det man går tilbake til, ikke det man begynner med.
 */
const CHOICES: { id: ThemeChoice; icon: LucideIcon }[] = [
  { id: "light", icon: Sun },
  { id: "dark", icon: Moon },
  { id: "system", icon: Monitor },
];

/**
 * Velgeren for lyst og mørkt tema.
 *
 * Bygget som samme knapperad som språkvelgeren ved siden av, med vilje: de to
 * er samme slags valg — hvordan appen skal se ut for meg — og to ulike
 * mønstre for det samme ville vært to ting å lære.
 *
 * Ikoner uten tekst. Sol, måne og skjerm er blant de få symbolene som leses
 * likt overalt, og raden står i toppen av siden der plassen er knappest.
 * `aria-label` bærer ordet for dem som trenger det.
 */
export function ThemeToggle({ choice, onChange, lang }: Props) {
  const t = dictionaries[lang];

  return (
    <div className="theme-switch no-print" role="group" aria-label={t.themeLabel}>
      {CHOICES.map(({ id, icon: Icon }) => (
        <button
          key={id}
          type="button"
          className={`theme-option ${id === choice ? "theme-option--on" : ""}`}
          aria-pressed={id === choice}
          aria-label={t.themeName[id]}
          title={t.themeName[id]}
          onClick={() => onChange(id)}
        >
          <Icon size={14} />
        </button>
      ))}
    </div>
  );
}
